import styled from 'styled-components';

import { Text } from '../../../../components/Text/Text';
import { COLORS, ANIM, GRADIENTS, MOBILE_MQ, SPACE } from '../../../../theme/tokens';
import { ArtStyleChip } from '../../../art-style/components/ArtStyleChip';
import type { TextAlign } from '../../custom-formats';

/* ── Format picker ──────────────────────────────────────────────────────────── */

export const PickerIntro = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 20px;
`;

export const PickerTitle = styled(Text).attrs({ tag: 'h2' as const })`
  font-size: 18px;
  font-weight: 700;
  color: ${COLORS.heading};
`;

export const PickerBody = styled(Text).attrs({ tag: 'paragraph' as const })`
  font-size: 13px;
  line-height: 19px;
  color: ${COLORS.textMuted};
`;

export const Group = styled.div`
  margin-bottom: 22px;
`;

export const GroupTitle = styled(Text).attrs({ tag: 'h3' as const })`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
  margin-bottom: 10px;
`;

export const FormatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 12px;
`;

export const FormatTile = styled.button`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 14px;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${COLORS.hairline};
  cursor: pointer;
  text-align: left;
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
    background: ${COLORS.surfaceMuted};
  }
`;

/* A proportional glyph of the format, centred in a fixed frame. */
export const FormatShapeFrame = styled.div`
  height: 84px;
  display: grid;
  place-items: center;
`;

export const FormatShape = styled.div<{ $ratio: number }>`
  /* Fit within an 84×120 frame while keeping the format's aspect ratio. */
  aspect-ratio: ${({ $ratio }) => $ratio};
  ${({ $ratio }) => ($ratio >= 1 ? 'width: 120px; max-height: 84px;' : 'height: 84px; max-width: 120px;')}
  border-radius: 6px;
  background: ${GRADIENTS.chipTint}, ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
`;

export const FormatName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
`;

export const FormatDim = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
  font-variant-numeric: tabular-nums;
`;

/* ── Saved drafts (My creatives) ─────────────────────────────────────────────── */

export const SavedGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(196px, 1fr));
  gap: 14px;
`;

export const NewTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 168px;
  border-radius: 14px;
  cursor: pointer;
  color: ${COLORS.textMuted};
  background: ${COLORS.surfaceFaint};
  border: 1px dashed ${COLORS.hairline};
  transition:
    border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE},
    color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    color: ${COLORS.text};
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const NewTileText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
`;

export const SavedCard = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 14px;
  overflow: hidden;
  background: ${COLORS.surfaceFaint};
  border: 1px solid ${COLORS.hairline};
`;

export const SavedPreview = styled.button<{ $bg: string; $image?: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 10;
  border: 0;
  cursor: pointer;
  padding: 0;
  background: ${({ $image, $bg }) => ($image ? `center / cover no-repeat url("${$image}")` : $bg)};
`;

export const SavedBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
`;

export const SavedInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const SavedName = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 13px;
  font-weight: 600;
  color: ${COLORS.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const SavedMeta = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

export const SavedActions = styled.div`
  display: flex;
  gap: 4px;
  flex: 0 0 auto;
`;

export const SavedBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  border: 0;
  cursor: pointer;
  background: transparent;
  color: ${COLORS.textMuted};

  &:hover {
    background: ${COLORS.hoverWash};
    color: ${COLORS.text};
  }
`;

/* ── Studio shell ───────────────────────────────────────────────────────────── */

/* Autosave status shown in the editor header ("Saving…" / "Saved"). No manual Save button —
   the editor persists on its own. */
export const SaveStatus = styled(Text).attrs({ tag: 'span' as const })`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: ${COLORS.textFaint};
  white-space: nowrap;
`;

/* Local editor header (back + title + actions). Deliberately NOT the shared BackHeader: that one
   is a sticky page-level drill-in that paints a solid header-bridge above itself, which fights a
   nested editor. This is a plain in-flow row. */
export const EditorHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 18px;
`;

export const EditorBack = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 999px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.text};
  cursor: pointer;
  flex: 0 0 auto;

  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const EditorTitle = styled(Text).attrs({ tag: 'span' as const })`
  flex: 1 1 auto;
  min-width: 0;
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.heading};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/* A labelled control block in the contextual panel (title above its control). */
export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

export const FieldTitle = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textFaint};
`;

export const ToolbarActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const StudioGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 320px;
  gap: 20px;
  align-items: start;

  @media ${MOBILE_MQ} {
    grid-template-columns: 1fr;
  }
`;

/* ── Canvas element (rendered inside the editor stage; sized + scaled by it) ───── */

export const Canvas = styled.div`
  position: relative;
  overflow: hidden;
  border-radius: 8px;
  container-type: size;
  box-shadow: 0 18px 48px -16px ${COLORS.pageBg};
  /* width + height are set inline (base px) by the editor stage, then scaled by its transform. */
`;

export const BgFill = styled.div<{ $bg: string; $image?: string; $ox: number; $oy: number }>`
  position: absolute;
  inset: 0;
  background: ${({ $image, $bg, $ox, $oy }) =>
    $image ? `${(0.5 + $ox) * 100}% ${(0.5 + $oy) * 100}% / cover no-repeat url("${$image}")` : $bg};
`;

export const Scrim = styled.div<{ $alpha: number }>`
  position: absolute;
  inset: 0;
  background: ${COLORS.pageBg};
  opacity: ${({ $alpha }) => $alpha};
`;

export const CanvasHeadline = styled.span<{ $scale: number; $color: string; $align: TextAlign; $width: number }>`
  display: block;
  font-family:
    'Satoshi',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  font-weight: 800;
  line-height: 1.05;
  font-size: ${({ $scale }) => $scale * 100}cqmin;
  color: ${({ $color }) => $color};
  width: ${({ $width }) => $width * 100}cqw;
  text-align: ${({ $align }) => $align};
  text-shadow: 0 1px 12px rgba(0, 0, 0, 0.28);
  white-space: pre-wrap;
  word-break: break-word;
`;

export const CanvasSub = styled.span<{ $scale: number; $color: string; $align: TextAlign; $width: number }>`
  display: block;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  font-weight: 500;
  line-height: 1.15;
  font-size: ${({ $scale }) => $scale * 100}cqmin;
  color: ${({ $color }) => $color};
  width: ${({ $width }) => $width * 100}cqw;
  text-align: ${({ $align }) => $align};
  text-shadow: 0 1px 10px rgba(0, 0, 0, 0.25);
  white-space: pre-wrap;
  word-break: break-word;
`;

export const CanvasCta = styled.span<{ $bg: string; $scale: number; $width: number; $height: number }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: ${({ $width }) => $width * 100}cqw;
  height: ${({ $height }) => $height * 100}cqh;
  padding: 0 3cqmin;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${COLORS.onAccent};
  font-family: -apple-system, BlinkMacSystemFont, sans-serif;
  font-weight: 700;
  font-size: ${({ $scale }) => $scale * 3.6}cqmin;
  line-height: 1;
  white-space: nowrap;
  overflow: hidden;
`;

export const CanvasLogo = styled.img<{ $scale: number }>`
  display: block;
  height: ${({ $scale }) => $scale * 100}cqmin;
  max-width: 50cqw;
  object-fit: contain;
`;

/* ── Controls panel ─────────────────────────────────────────────────────────── */

export const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const Section = styled.section`
  padding: 14px 0;
  border-top: 1px solid ${COLORS.hairline};

  &:first-of-type {
    border-top: 0;
    padding-top: 0;
  }
`;

export const SectionLabel = styled(Text).attrs({ tag: 'h4' as const })`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
  margin-bottom: 10px;
`;

export const RowBetween = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const MiniLabel = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  font-weight: 500;
  color: ${COLORS.textMuted};
`;

export const Input = styled.input`
  width: 100%;
  padding: 9px 11px;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
  &:focus {
    outline: none;
    border-color: ${COLORS.hoverBorderLit};
  }
`;

/** The brand's active art style and the mascot picker above the generate prompt, at their natural widths. Both
 *  hide without an active style, and the row goes with them (so the column's gap never doubles). */
export const ArtStyleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${SPACE[8]};

  &:empty {
    display: none;
  }
`;

export const StudioArtStyleChip = styled(ArtStyleChip)`
  align-self: flex-start;
`;

export const Prompt = styled.textarea`
  width: 100%;
  min-height: 66px;
  resize: vertical;
  padding: 9px 11px;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};
  border: 1px solid ${COLORS.hairline};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 13px;
  line-height: 1.4;

  &::placeholder {
    color: ${COLORS.textFaint};
  }
  &:focus {
    outline: none;
    border-color: ${COLORS.hoverBorderLit};
  }
`;

export const SwatchRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
`;

export const Swatch = styled.button<{ $color: string; $selected: boolean }>`
  width: 26px;
  height: 26px;
  border-radius: 8px;
  cursor: pointer;
  background: ${({ $color }) => $color};
  border: 1px solid ${COLORS.hairline};
  box-shadow: ${({ $selected }) =>
    $selected ? `0 0 0 2px ${COLORS.pageBg}, 0 0 0 4px ${COLORS.hoverBorderLit}` : 'none'};
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    transform: translateY(-1px);
  }
`;

export const GradientRow = styled.div`
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 7px;
`;

export const GradientSwatch = styled.button<{ $bg: string; $selected: boolean }>`
  height: 34px;
  border-radius: 9px;
  cursor: pointer;
  background: ${({ $bg }) => $bg};
  border: 1px solid ${COLORS.hairline};
  box-shadow: ${({ $selected }) =>
    $selected ? `0 0 0 2px ${COLORS.pageBg}, 0 0 0 4px ${COLORS.hoverBorderLit}` : 'none'};
`;

export const SegRow = styled.div`
  display: inline-flex;
  padding: 3px;
  border-radius: 10px;
  background: ${COLORS.surfaceInset};
  gap: 2px;
`;

export const SegBtn = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $active }) => ($active ? COLORS.textBright : COLORS.textMuted)};
  background: ${({ $active }) => ($active ? COLORS.surfaceCard : 'transparent')};

  &:hover {
    color: ${COLORS.textBright};
  }
`;

/* Full-width segmented control (segments share the row evenly) — for the panel's Size / Align
   pickers so they line up instead of hugging their content at different widths. */
export const SegRowFull = styled(SegRow)`
  display: flex;
  width: 100%;

  & > button {
    flex: 1 1 0;
    text-align: center;
  }
`;

/* Compact number slider used for the logo size in the contextual panel. */
export const Slider = styled.input`
  width: 100%;
  accent-color: ${COLORS.accent};
  cursor: pointer;
`;

export const HintText = styled(Text).attrs({ tag: 'span' as const })`
  font-size: 12px;
  line-height: 17px;
  color: ${COLORS.textFaint};
`;

/* Upload-your-own-background control (a styled <label> wrapping a hidden file input). */
export const UploadBtn = styled.label`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 9px 12px;
  border-radius: 10px;
  border: 1px dashed ${COLORS.hairline};
  background: ${COLORS.surfaceFaint};
  color: ${COLORS.text};
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;

  input {
    display: none;
  }
  &:hover {
    border-color: ${COLORS.hoverBorderLit};
  }
`;

/* Pick-from-your-existing-banners strip — two rows so it isn't crowded. */
export const ImageRow = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-template-rows: repeat(2, 42px);
  grid-auto-columns: 66px;
  gap: 8px;
  overflow-x: auto;
  padding-bottom: 4px;
`;

export const ImageThumb = styled.button<{ $image: string; $selected: boolean }>`
  width: 66px;
  height: 42px;
  border-radius: 8px;
  cursor: pointer;
  padding: 0;
  background: center / cover no-repeat url('${({ $image }) => $image}');
  border: 1px solid ${({ $selected }) => ($selected ? COLORS.accent : COLORS.hairline)};

  &:hover {
    border-color: ${({ $selected }) => ($selected ? COLORS.accent : COLORS.hoverBorderLit)};
  }
`;

export const FieldGap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 9px;
`;

export const Toggle = styled.button<{ $on: boolean }>`
  position: relative;
  width: 38px;
  height: 22px;
  border-radius: 999px;
  border: 0;
  cursor: pointer;
  background: ${({ $on }) => ($on ? COLORS.badgeGreen : COLORS.toggleTrackOff)};
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &::after {
    content: '';
    position: absolute;
    top: 3px;
    left: ${({ $on }) => ($on ? '19px' : '3px')};
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: ${COLORS.onAccent};
    transition: left ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  }
`;
